// src/features/form/BuilderTab.tsx
// Field palette + field list + field editor. The form-kit (FormRow etc.) does
// the field-level editing inside FieldEditorPanel; this component owns the
// CRUD/reorder wiring (CreateFormField, UpdateFormField, DeleteFormField,
// ReorderFormFields) and the palette of FormFieldType buttons.

import { useState } from 'react';
import { observer } from '@react';
import { useI18n } from '@react/hooks';
import { Alert } from '@react/components';
import type { Form, FormField, FormFieldType } from '@core/types/form';
import { formApi } from '@core/api';
import { FieldEditorPanel } from './FieldEditorPanel';
import { FIELD_TYPE_ORDER, errMessage } from './helpers';

export interface BuilderTabProps {
  form: Form;
  onFormChanged: (form: Form) => void;
}

function fieldTypeLabel(t: (k: string) => string, type: FormFieldType): string {
  return t(`form.fieldType.${type.replace('FORM_FIELD_TYPE_', '').toLowerCase()}`);
}

export const BuilderTab = observer(({ form, onFormChanged }: BuilderTabProps) => {
  const { t } = useI18n();
  const [error, setError] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const formId = form.entity?.id ?? '';
  const page = form.pages[0];
  const pageId = page?.id || 'page-1';
  const fields = page?.fields ?? [];
  const selectedField = fields.find((f) => f.entity?.id === selectedFieldId) ?? null;

  const refresh = async () => {
    const res = await formApi.GetForm(formId);
    if (res.form) onFormChanged(res.form);
  };

  const addField = async (type: FormFieldType) => {
    setBusy(true);
    setError(null);
    try {
      const res = await formApi.CreateFormField(formId, {
        entity: {
          type_id: '',
          type_name: 'form_field',
          id: '',
          platform_id: form.entity?.platform_id ?? '',
          tenant_id: form.entity?.tenant_id ?? '',
          product_id: form.entity?.product_id ?? '',
        },
        form: form.entity!,
        page_id: pageId,
        label: t('form.builder.newFieldLabel'),
        field_type: type,
        options: [],
        order: fields.length,
      });
      await refresh();
      if (res.field?.entity?.id) setSelectedFieldId(res.field.entity.id);
    } catch (e) {
      setError(errMessage(e, t('form.builder.fieldSaveError')));
    } finally {
      setBusy(false);
    }
  };

  const updateField = async (field: FormField, patch: Partial<FormField>) => {
    if (!field.entity?.id) return;
    setBusy(true);
    setError(null);
    try {
      await formApi.UpdateFormField(field.entity.id, { field: { ...field, ...patch } });
      await refresh();
    } catch (e) {
      setError(errMessage(e, t('form.builder.fieldSaveError')));
    } finally {
      setBusy(false);
    }
  };

  const deleteField = async (field: FormField) => {
    if (!field.entity?.id) return;
    setBusy(true);
    setError(null);
    try {
      await formApi.DeleteFormField(field.entity.id);
      setSelectedFieldId(null);
      await refresh();
    } catch (e) {
      setError(errMessage(e, t('form.builder.fieldSaveError')));
    } finally {
      setBusy(false);
    }
  };

  const move = async (index: number, delta: number) => {
    const target = index + delta;
    if (target < 0 || target >= fields.length) return;
    const ids = fields.map((f) => f.entity?.id ?? '');
    const next = [...ids];
    [next[index], next[target]] = [next[target], next[index]];
    setBusy(true);
    setError(null);
    try {
      await formApi.ReorderFormFields(formId, { form: form.entity!, page_id: pageId, field_ids: next });
      await refresh();
    } catch (e) {
      setError(errMessage(e, t('form.builder.reorderError')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_1fr_360px]">
      {/* Palette */}
      <div className="space-y-1">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {t('form.builder.palette')}
        </p>
        <div className="grid grid-cols-2 gap-1 lg:grid-cols-1">
          {FIELD_TYPE_ORDER.map((type) => (
            <button
              key={type}
              type="button"
              disabled={busy}
              onClick={() => addField(type)}
              className="rounded-md border border-dashed border-border px-2 py-1.5 text-left text-xs text-muted-foreground hover:border-primary hover:text-foreground disabled:opacity-50"
            >
              + {fieldTypeLabel(t, type)}
            </button>
          ))}
        </div>
      </div>

      {/* Field list */}
      <div className="space-y-1.5">
        {error && <Alert variant="error">{error}</Alert>}
        {fields.length === 0 && (
          <div className="rounded-md border border-dashed border-border p-8 text-center">
            <p className="text-sm text-muted-foreground">{t('form.builder.noFields')}</p>
          </div>
        )}
        {fields.map((f, i) => (
          <div
            key={f.entity?.id ?? i}
            className={`flex items-center gap-2 rounded-md border p-2 ${
              selectedFieldId === f.entity?.id ? 'border-primary bg-accent/40' : 'border-border'
            }`}
          >
            <div className="flex flex-col">
              <button
                type="button"
                disabled={busy || i === 0}
                onClick={() => move(i, -1)}
                aria-label={t('form.builder.moveUp')}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                ▲
              </button>
              <button
                type="button"
                disabled={busy || i === fields.length - 1}
                onClick={() => move(i, 1)}
                aria-label={t('form.builder.moveDown')}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30"
              >
                ▼
              </button>
            </div>
            <button
              type="button"
              onClick={() => setSelectedFieldId(f.entity?.id ?? null)}
              className="min-w-0 flex-1 truncate text-left text-sm text-foreground"
            >
              {f.label || t('form.builder.newFieldLabel')}
              <span className="ml-2 text-xs text-muted-foreground">{fieldTypeLabel(t, f.field_type ?? 'FORM_FIELD_TYPE_TEXT')}</span>
              {f.validation?.required && <span className="ml-1 text-destructive">*</span>}
            </button>
          </div>
        ))}
      </div>

      {/* Editor */}
      <div className="rounded-md border border-border p-3">
        {!selectedField && (
          <p className="text-sm text-muted-foreground">{t('form.builder.selectField')}</p>
        )}
        {selectedField && (
          <FieldEditorPanel
            key={selectedField.entity?.id}
            field={selectedField}
            isSaving={busy}
            onChange={(patch) => updateField(selectedField, patch)}
            onDelete={() => deleteField(selectedField)}
          />
        )}
      </div>
    </div>
  );
});
